import { memo, useEffect } from 'react'
import { useCurrentWindowToSelection, useIsCurrentWindowAttached, useWindowSelection } from '../../../../store/atoms'
import { useAddWindow } from '../../../../hooks/useAddWindow'
import { logAmplitudeEvent } from '../../../../utils'

/** Toggles the focused window's context on Ctrl+Tab (Windows) / Alt+Tab (macOS). */
export const WindowContextTabToggleHandler = memo(() => {
  const currentWindow = useCurrentWindowToSelection()
  const isCurrentWindowAttached = useIsCurrentWindowAttached()
  const addWindow = useAddWindow()
  const { removeSelectedWindow, isWindowSelected } = useWindowSelection()

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') {
        return
      }

      const isWindows = process.platform === 'win32'
      const isContextToggleModifier = isWindows ? event.ctrlKey && !event.altKey : event.altKey && !event.ctrlKey

      if (!isContextToggleModifier || event.shiftKey || event.metaKey) {
        return
      }

      event.preventDefault()
      event.stopPropagation()

      if (!currentWindow) {
        return
      }

      // Toggle window context: add if not selected, remove if selected
      const isSelected = isWindowSelected(currentWindow)

      if (isSelected) {
        // Remove the window context
        removeSelectedWindow(currentWindow)
        logAmplitudeEvent('Remove Window Context', {
          appName: currentWindow.appName,
          focusedWindow: true,
          trigger: 'Keyboard Shortcut',
        })
      } else if (!isCurrentWindowAttached) {
        // Add the window context (only if not already auto-attached)
        addWindow(currentWindow)
        logAmplitudeEvent('Add Window Context', {
          appName: currentWindow.appName,
          focusedWindow: true,
          trigger: 'Keyboard Shortcut',
        })
      }
      // Note: if isCurrentWindowAttached is true, the window is already auto-attached
      // so we don't need to do anything (it can't be toggled off in this state)
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [currentWindow, isCurrentWindowAttached, addWindow, removeSelectedWindow, isWindowSelected])

  return null
})
WindowContextTabToggleHandler.displayName = 'WindowContextTabToggleHandler'
